import { useEffect, useState } from "react";
import { Loader2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, useContent, resolveImg } from "../../lib/api";
import { formatApiError, useMember } from "../../lib/memberAuth";

const inp = "w-full border border-black/15 rounded-xl px-4 py-3 text-sm focus:border-[#E63946] outline-none";
const L = ({ children }) => <label className="block text-xs uppercase tracking-[0.18em] text-black/50 mb-2">{children}</label>;
const STATUS_STYLE = {
  submitted: "bg-black/5 text-black/60", under_review: "bg-yellow-500/10 text-yellow-600",
  approved: "bg-emerald-500/10 text-emerald-600", waitlisted: "bg-blue-500/10 text-blue-600",
  info_needed: "bg-orange-500/10 text-orange-600", declined: "bg-black/5 text-black/40",
};
export const statusLabel = (s) => ({ submitted: "Submitted", under_review: "Under Review", approved: "Active", waitlisted: "Waitlisted", info_needed: "More Info Needed", declined: "Declined" }[s] || s);

export const MinistriesSection = () => {
  const { data: ministries, loading } = useContent("ministries");
  const { member } = useMember();
  const [apps, setApps] = useState(null);
  const [applying, setApplying] = useState(null);
  const [form, setForm] = useState({ phone: "", why: "", experience: "", availability: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (member?.phone) setForm((f) => ({ ...f, phone: f.phone || member.phone })); }, [member]);

  const loadApps = () => api.get("/members/my-ministries").then((r) => setApps(r.data)).catch(() => setApps([]));
  useEffect(() => { loadApps(); }, []);

  const appFor = (mid) => (apps || []).find((a) => a.ministry_id === mid && a.status !== "declined");

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/members/ministries/apply", { ministry_id: applying.id, ministry_name: applying.name, ...form });
      toast.success("Application submitted! We'll be in touch.");
      setApplying(null); setForm({ phone: "", why: "", experience: "", availability: "" });
      loadApps();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };

  if (loading || !apps) return <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div>;

  const mine = (apps || []).filter((a) => a.status === "approved");
  const pending = (apps || []).filter((a) => a.status !== "approved");

  return (
    <div data-testid="ministries-section">
      {mine.length > 0 && (
        <div className="mb-8">
          <h3 className="font-display font-bold text-lg mb-3">My Ministries</h3>
          <div className="space-y-2">
            {mine.map((a) => (
              <div key={a.id} className="bg-white border border-black/10 rounded-xl p-4 flex items-center gap-3" data-testid="my-ministry-row">
                <Users size={18} className="text-[#E63946]" />
                <div className="flex-1">
                  <p className="font-medium text-sm">{a.ministry_name}</p>
                  <p className="text-xs text-black/45">Joined {new Date(a.updated_at || a.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
                </div>
                <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600">Active</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {pending.length > 0 && (
        <div className="mb-8">
          <h3 className="font-display font-bold text-lg mb-3">My Applications</h3>
          <div className="space-y-2">
            {pending.map((a) => (
              <div key={a.id} className="bg-white border border-black/10 rounded-xl p-4 flex items-center gap-3" data-testid="ministry-app-row">
                <div className="flex-1">
                  <p className="font-medium text-sm">{a.ministry_name}</p>
                  <p className="text-xs text-black/45">Applied {new Date(a.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`text-xs px-3 py-1.5 rounded-full ${STATUS_STYLE[a.status] || ""}`} data-testid="ministry-app-status">{statusLabel(a.status)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <h3 className="font-display font-bold text-lg mb-3">Browse Ministries & Groups</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        {(ministries || []).map((min) => {
          const a = appFor(min.id);
          return (
            <div key={min.id} className="bg-white border border-black/10 rounded-xl overflow-hidden" data-testid="ministry-card">
              {min.image && <img src={resolveImg(min.image)} alt={min.name} className="w-full h-36 object-cover" />}
              <div className="p-5">
                <p className="font-display font-bold">{min.name}</p>
                {min.leader && <p className="text-xs text-black/45 mt-0.5">Led by {min.leader}</p>}
                {min.meeting && <p className="text-xs text-[#E63946] mt-0.5">{min.meeting}</p>}
                {min.desc && <p className="text-sm text-black/55 mt-2 line-clamp-3">{min.desc}</p>}
                <div className="mt-4">
                  {a ? (
                    <span className={`text-xs px-3 py-1.5 rounded-full ${STATUS_STYLE[a.status] || ""}`}>{a.status === "approved" ? "You're a member" : statusLabel(a.status)}</span>
                  ) : (
                    <button onClick={() => setApplying(min)} data-testid={`apply-ministry-btn-${min.id}`}
                      className="rounded-full bg-[#0A0A0A] text-white px-5 py-2.5 text-sm hover:bg-[#E63946] transition-colors">Apply to Join</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {applying && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4 overflow-y-auto" onClick={() => setApplying(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md my-8" onClick={(e) => e.stopPropagation()} data-testid="ministry-apply-modal">
            <div className="flex items-center justify-between p-5 border-b border-black/10">
              <h3 className="font-display font-bold text-lg">Apply — {applying.name}</h3>
              <button onClick={() => setApplying(null)} className="grid place-items-center w-9 h-9 rounded-full hover:bg-black/5"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div><L>Phone</L><input className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><L>Why are you interested?</L><textarea rows={3} className={inp} value={form.why} onChange={(e) => setForm({ ...form, why: e.target.value })} data-testid="apply-why-input" /></div>
              <div><L>Previous experience (optional)</L><textarea rows={2} className={inp} value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} /></div>
              <div><L>Availability</L><input className={inp} placeholder="e.g. Sundays and Tuesday evenings" value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} /></div>
              <button onClick={submit} disabled={busy} data-testid="submit-ministry-app-btn"
                className="w-full rounded-full bg-[#E63946] text-white py-3.5 font-medium hover:bg-[#BA1B25] transition-colors disabled:opacity-60">
                {busy ? "Submitting…" : "Submit Application"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
